import React, { useState, useEffect } from 'react';
import { SITE_CONFIG, trackEvent } from '../config/site';
import { X, CheckCircle2, Sparkles, Send, Lock } from 'lucide-react';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPlan?: string;
  onOpenLegal: (type: 'privacy' | 'terms' | 'ai') => void;
}

export const WaitlistModal: React.FC<WaitlistModalProps> = ({
  isOpen,
  onClose,
  initialPlan,
  onOpenLegal,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profile, setProfile] = useState('especialista');
  const [plan, setPlan] = useState(initialPlan || 'Pro');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialPlan) {
      setPlan(initialPlan);
    }
  }, [initialPlan]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Por favor, informe seu nome completo.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Por favor, informe um endereço de e-mail corporativo ou pessoal válido.');
      return;
    }
    if (!consent) {
      setError('É necessário concordar com a política de privacidade para submeter a solicitação.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${SITE_CONFIG.apiUrl}/api/public/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          professionalProfile: profile,
          planInterest: plan,
          consent: true,
          consentVersion: '2026.1',
          source: 'sales_page',
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Não foi possível registrar seu interesse no momento.');
      }

      // Track non-PII analytics
      trackEvent('waitlist_submit', {
        profile,
        selectedPlan: plan,
      });
      trackEvent('waitlist_success', {
        selectedPlan: plan,
      });

      setSubmitted(true);
      setLoading(false);
    } catch (err: unknown) {
      setLoading(false);
      trackEvent('waitlist_error', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      setError(
        err instanceof Error
          ? err.message
          : 'Falha de comunicação com o servidor. Verifique sua conexão e tente novamente.'
      );
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state after transition completes
    setTimeout(() => {
      setSubmitted(false);
      setError(null);
    }, 200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div
        className="w-full max-w-lg rounded-3xl bg-brand-surface border border-brand-border p-6 sm:p-8 shadow-2xl relative overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 p-2 rounded-xl text-brand-textSubtle hover:text-white hover:bg-white/5 transition-all focus:outline-none focus:ring-2 focus:ring-brand-primary"
          aria-label="Fechar janela"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-400 mx-auto flex items-center justify-center border border-emerald-500/30">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <h3 id="modal-headline" className="text-2xl font-bold text-white">
              Solicitação Registrada!
            </h3>

            <p className="text-sm text-brand-textMuted leading-relaxed max-w-sm mx-auto">
              Obrigado pelo interesse no InfoForge. Nossa equipe analisa os perfis para balancear a demanda do lote atual e entrará em contato via e-mail com as instruções de acesso assim que novas vagas forem abertas.
            </p>

            <div className="p-4 rounded-xl bg-brand-bg border border-brand-border/60 text-xs text-brand-textSubtle font-mono">
              STATUS: NA FILA DO PRIVATE BETA (LOTE 1)
            </div>

            <button
              onClick={handleClose}
              className="w-full py-3.5 rounded-xl bg-brand-surfaceLight hover:bg-white/10 text-white text-sm font-semibold transition-colors"
            >
              Concluir
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-brand-cyan uppercase tracking-wider mb-1">
              <Sparkles className="w-4 h-4" />
              <span>Private Beta · Lote 1</span>
            </div>

            <h3 id="modal-headline" className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              Solicitar Acesso ao InfoForge
            </h3>

            <p className="text-xs sm:text-sm text-brand-textMuted leading-relaxed">
              Preencha os dados abaixo para entrar na fila prioritária de avaliação para o Private Beta Fechado.
            </p>

            {error && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs">
                {error}
              </div>
            )}

            <div className="space-y-3 pt-2">
              <div>
                <label className="block text-xs font-medium text-brand-text mb-1">
                  Seu Nome Completo
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Dra. Mariana Silva"
                  className="w-full px-4 py-2.5 rounded-xl bg-brand-bg border border-brand-border text-sm text-white placeholder:text-brand-textSubtle focus:outline-none focus:border-brand-primary transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-text mb-1">
                  E-mail Profissional
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="mariana@exemplo.com.br"
                  className="w-full px-4 py-2.5 rounded-xl bg-brand-bg border border-brand-border text-sm text-white placeholder:text-brand-textSubtle focus:outline-none focus:border-brand-primary transition-colors"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-brand-text mb-1">
                    Seu Perfil
                  </label>
                  <select
                    value={profile}
                    onChange={(e) => setProfile(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-brand-bg border border-brand-border text-xs text-white focus:outline-none focus:border-brand-primary"
                  >
                    <option value="especialista">Especialista / Autor</option>
                    <option value="coprodutor">Coprodutor / Lançador</option>
                    <option value="agencia">Agência de Conteúdo</option>
                    <option value="empresa">Empresa / Treinamento B2B</option>
                    <option value="outro">Outro Perfil</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-brand-text mb-1">
                    Plano de Interesse
                  </label>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-brand-bg border border-brand-border text-xs text-white focus:outline-none focus:border-brand-primary"
                  >
                    <option value="Starter">Starter (700 cr)</option>
                    <option value="Pro">Pro (1.700 cr)</option>
                    <option value="Premium">Premium (3.000 cr)</option>
                  </select>
                </div>
              </div>

              {/* LGPD Consent */}
              <div className="pt-2">
                <label className="flex items-start gap-2.5 cursor-pointer text-xs text-brand-textMuted select-none">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 rounded border-brand-border text-brand-primary focus:ring-brand-primary bg-brand-bg"
                  />
                  <span className="leading-snug">
                    Concordo em receber contato e comunicações sobre o InfoForge em conformidade com a LGPD e a{' '}
                    <button
                      type="button"
                      onClick={() => onOpenLegal('privacy')}
                      className="text-brand-cyan hover:underline"
                    >
                      Política de Privacidade
                    </button>
                    .
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-95 transition-all shadow-lg shadow-brand-primary/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span>Processando solicitação...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Enviar Solicitação de Convite</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-brand-textSubtle pt-2">
              <Lock className="w-3.5 h-3.5 text-emerald-400" />
              <span>Seus dados nunca serão compartilhados com terceiros.</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
